import { Injectable } from 'angular-ts-decorators';

import { CurrentSession } from './current-session';
import { Domain } from './domain';
import { Session } from './session';
import type { SessionStorage } from './session-storage';
import { Sentry } from '@shared/app/scripts/services/sentry';
import { SessionRequests } from './session-requests';

/**
 * Service that allows for changing the current session.
 */
@Injectable('sessionManager')
export class SessionManager {
  private SESSION_COOKIE: string;
  private TENANT_COOKIE: string;

  constructor(
    private $q: ng.IQService,
    private $window: ng.IWindowService,
    private currentSession: CurrentSession,
    private domain: Domain,
    private ipCookie,
    private sentry: Sentry,
    private session: Session,
    private sessionRequests: SessionRequests,
    private sessionStorage: SessionStorage,
  ) {
    'ngInject';

    this.SESSION_COOKIE = 'sid';
    this.TENANT_COOKIE = 'tenantId';
  }

  /**
   *
   * Attempts to login
   * Can deal with basic auth and 2FA
   *
   * @param {string} username
   * user id usually email
   * @param {string} password
   * password
   * @param {boolean} rememberMe
   * Boolean that indicates if user whats to be logged in longer then default 48h.
   * @returns {httpPromise} it will resolve with updated currentSession object or a 2FA token
   */
  login(
    username: string,
    password: string,
    rememberMe: boolean,
  ): ng.IPromise<any> {
    const loginDeferred = this.$q.defer();

    this.sessionRequests.login(username, password, rememberMe).then(
      (response) => {
        if (!response['2FAToken']) {
          this.session.init();
        }

        if (response['2FAToken']) {
          loginDeferred.resolve(response);
        } else {
          loginDeferred.resolve(this.currentSession);
        }
      },
      (error) => {
        // login failed
        loginDeferred.reject(error);
      },
    );

    return loginDeferred.promise;
  }

  /**
   *
   * Sends 2FA token and code to backend and starts sessionManager and returns session info.
   *
   * @param {string} token
   * the 2FA token returned earlier by login
   * @param {string} code
   * the login code from the 2FA authetication app
   * @returns {httpPromise} it will resolve with updated currentSession object
   */
  loginTwoFa(token: string, code: string): ng.IPromise<any> {
    const loginDeferred = this.$q.defer();

    this.sessionRequests
      .loginTwoFa(token, code)
      .then(() => this.session.init())
      .then(
        () => {
          loginDeferred.resolve(this.currentSession);
        },
        (error) => {
          loginDeferred.reject(error);
        },
      );

    return loginDeferred.promise;
  }

  /**
   *
   * Sets tenant and update the sessionManager information.
   *
   * @param {string} tenantId
   * Current user need to have a permission to work as the tenant.
   * @returns {httpPromise} it will resolve with /set-tenant response, or reject with
   * error
   */
  setTenant(tenantId: string): ng.IPromise<any> {
    return this.sessionRequests.setTenant(tenantId).then(
      (setTenantResponse) => {
        this.ipCookie(this.TENANT_COOKIE, tenantId, {
          domain: this.domain.getParent(),
          path: '/',
        });

        this.session.init();

        return setTenantResponse;
      },
      (error) => {
        this.logout();

        return this.$q.reject(error);
      },
    );
  }

  /**
   *
   * Remove the sessionManager cookie & request deleting the sessionManager on the server
   * @return {boolean}
   * true if it's redirecting the page, false otherwise
   */
  logout(): ng.IPromise<any> {
    const sid = this.ipCookie(this.SESSION_COOKIE);

    this.currentSession.empty();
    this.sessionStorage.clear();
    this.sentry.setUser();

    return this.sessionRequests.logout(sid).then(
      () => {
        this.ipCookie.remove(this.SESSION_COOKIE, {
          domain: this.domain.getParent(),
          path: '/',
        });
        this.ipCookie.remove(this.TENANT_COOKIE, {
          domain: this.domain.getParent(),
          path: '/',
        });

        if (this.session.redirectAnonymousToGate) {
          this.$window.location.href = this.domain.link('www');

          return true;
        }

        this.session.init(); // re init, if staying where it was

        return false;
      },
      // Clear cookies and session to remove the user from a limbo logout state
      (error) => {
        this.ipCookie.remove(this.SESSION_COOKIE, {
          domain: this.domain.getParent(),
          path: '/',
        });

        this.currentSession.empty();

        return this.$q.reject(error);
      },
    );
  }
}
